<script setup lang="ts">
    import DataTableActions from '@/components/datatable/DataTableActions.vue';
    import { Payslip } from '@/types';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import { computed, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { CircleEllipsis } from 'lucide-vue-next';
    import {
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuTrigger,
    } from '@/components/ui/dropdown-menu';
    import { Button } from '@/components/ui/button';
    import { popupCenterDual } from '@/utils/helpers';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData } from '@/types';
    import axios from 'axios';
    import PayslipUpload from './PayslipUpload.vue';
    import PayslipSendReminder from './PayslipSendReminder.vue';
    import PayslipDelete from './PayslipDelete.vue';
    import PayslipApprove from './PayslipApprove.vue';
    import PayslipReject from './PayslipReject.vue';
    import { toast } from 'vue-sonner';
    import AddButton from '@/components/AddButton.vue';
    import QueryKey from '@/enums/QueryKey';
    import { useQueryClient } from '@tanstack/vue-query';

    const props = defineProps<{
        payslip: Payslip;
    }>();

    const page = usePage<SharedData>();
    const user = computed(() => page.props.auth.user);

    const uploadModalOpen = ref(false);
    const sendReminderModalOpen = ref(false);
    const deleteModalOpen = ref(false);
    const approveModalOpen = ref(false);
    const rejectModalOpen = ref(false);

    const queryClient = useQueryClient();
    const { t } = useI18n();

    const isFinalWithoutCustomer = computed(() => {
        return props.payslip.status === 'upload-final-without-customer';
    });

    const isApprovedWithoutCustomer = computed(() => {
        return (
            props.payslip.status !== 'approved' &&
            props.payslip.status !== 'upload-final' &&
            props.payslip.sent_to_payroll_system_at
        );
    });

    const isRejected = computed(() => {
        return props.payslip.status === 'rejected';
    });

    const isApproved = computed(() => {
        return props.payslip.status === 'approved';
    });

    const isUploaded = computed(() => {
        return props.payslip.status === 'uploaded';
    });

    const isFinal = computed(() => {
        return props.payslip.status === 'upload-final';
    });

    const canUpdatePayslips = computed(() => {
        return !user.value.is_client && hasPermission(Permission.SALARY_PAYSLIPS_UPDATE);
    });

    const isFirstApprover = computed(() => {
        return user.value.id == props.payslip.company.salary_approval_steps?.first_approver_id;
    });

    const isSecondApprover = computed(() => {
        return user.value.id == props.payslip.company.salary_approval_steps?.next_approver_id;
    });

    const isApprovable = computed(() => {
        if (!isUploaded.value || !user.value.is_client) {
            return false;
        }
        if (
            props.payslip.company.salary_two_step_approval &&
            props.payslip.read_by_first_approver &&
            isFirstApprover
        ) {
            return true;
        } else if (
            props.payslip.company.salary_two_step_approval &&
            props.payslip.read_by_next_approver &&
            isSecondApprover.value
        ) {
            return true;
        } else if (
            !props.payslip.company.salary_two_step_approval &&
            props.payslip.read_by_client
        ) {
            return true;
        }

        return false;
    });

    const hasSomeStatus = computed(() => {
        return (
            isFinalWithoutCustomer.value ||
            isApprovedWithoutCustomer.value ||
            isRejected.value ||
            isApproved.value ||
            isUploaded.value ||
            isFinal.value
        );
    });

    const text = computed(() => {
        if (isFinalWithoutCustomer.value) {
            return t('forms.upload-final-without-customer');
        } else if (isApprovedWithoutCustomer.value) {
            return t('forms.approved-without-customer');
        } else if (isRejected.value) {
            return t('forms.rejected');
        } else if (isApproved.value) {
            return t('forms.approved');
        } else if (isUploaded.value) {
            if (!user.value.is_client) {
                return t('forms.uploaded');
            } else {
                if (props.payslip.company.salary_two_step_approval) {
                    if (
                        (!props.payslip.user && isFirstApprover.value) ||
                        (props.payslip.user && props.payslip.user.id !== user.value.id)
                    ) {
                        return t('pay-slips.awaiting_approval');
                    } else {
                        return t('pay-slips.awaiting_other_approver');
                    }
                } else {
                    return t('pay-slips.awaiting_approval');
                }
            }
        } else if (isFinal.value) {
            return t('forms.upload-final');
        }

        return t('forms.not_uploaded');
    });

    const color = computed(() => {
        if (isFinalWithoutCustomer.value || isFinal.value || isUploaded.value) {
            return 'text-primary';
        } else if (isApprovedWithoutCustomer.value || isApproved.value) {
            return 'text-warning';
        } else if (isRejected.value) {
            return 'text-destructive';
        }

        return 'text-destructive';
    });

    const { hasPermission } = usePermissions();

    const openFile = (url: string, name?: string) => {
        if (!props.payslip.read_by_client && user.value.is_client) {
            markAsRead();
        }

        popupCenterDual(url, name || 'preview', window.innerWidth / 2, window.innerHeight / 1.1);
    };

    const openFileWithApprovers = (url: string, name?: string) => {
        if (
            props.payslip.company.salary_two_step_approval &&
            user.value.is_client &&
            !props.payslip.read_by_first_approver &&
            isFirstApprover.value
        ) {
            markAsReadByFirstApprover();
        } else if (
            props.payslip.company.salary_two_step_approval &&
            user.value.is_client &&
            !props.payslip.read_by_next_approver &&
            isSecondApprover.value
        ) {
            markAsReadBySecondApprover();
        }

        if (!props.payslip.read_by_client && user.value.is_client) {
            markAsRead();
        }

        popupCenterDual(url, name || 'preview', window.innerWidth / 2, window.innerHeight / 1.1);
    };

    const markAsReadByFirstApprover = () => {
        axios
            .post(route('v2.salary.payslip.mark-as-read-by-first-approver'), {
                id: props.payslip.id,
            })
            .then(() => {
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLLS, user.value.selected_company_id],
                });
            });
    };

    const markAsReadBySecondApprover = () => {
        axios
            .post(route('v2.salary.payslip.mark-as-read-by-next-approver'), {
                id: props.payslip.id,
            })
            .then(() => {
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLLS, user.value.selected_company_id],
                });
            });
    };

    const markAsRead = () => {
        axios
            .post(route('v2.salary.payslip.mark-as-read'), {
                id: props.payslip.id,
            })
            .then(() => {
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLLS, user.value.selected_company_id],
                });
            });
    };

    const openUploadDialog = () => {
        uploadModalOpen.value = true;
    };

    const openSendReminderDialog = () => {
        sendReminderModalOpen.value = true;
    };

    const openDeleteDialog = () => {
        deleteModalOpen.value = true;
    };

    const openApproveDialog = () => {
        if (isSecondApprover.value && !props.payslip.approved_by) {
            toast.error(t('salary.pay_slips.two_step_approval.cannot_approve'));
            return;
        }
        approveModalOpen.value = true;
    };

    const openRejectDialog = () => {
        if (isSecondApprover.value && !props.payslip.approved_by) {
            toast.error(t('salary.pay_slips.two_step_approval.cannot_approve'));
            return;
        }
        rejectModalOpen.value = true;
    };
</script>

<template>
    <DataTableActions :data="payslip">
        <div
            v-if="hasSomeStatus"
            class="flex items-center gap-1"
            :class="color"
        >
            <span class="uppercase">{{ text }}</span>

            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button
                        variant="inherit"
                        size="xs"
                        width="narrow"
                    >
                        <CircleEllipsis />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                    <DropdownMenuItem
                        v-if="isUploaded"
                        @click="
                            openFileWithApprovers(
                                route('v2.salary.payslip.get', { paySlipId: payslip.id }),
                            )
                        "
                    >
                        {{ t('pay-slips.open') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        v-else-if="hasSomeStatus"
                        @click="openFile(route('v2.salary.payslip.get', { paySlipId: payslip.id }))"
                    >
                        {{ t('pay-slips.open') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        v-if="canUpdatePayslips"
                        @click="openUploadDialog"
                    >
                        {{ t('pay-slips.upload') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        v-if="canUpdatePayslips && !isRejected && !isApproved && hasSomeStatus"
                        @click="openSendReminderDialog"
                    >
                        {{ t('pay-slips.reminder-text') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        v-if="isUploaded && hasPermission(Permission.SALARY_PAYSLIPS_DELETE)"
                        @click="openDeleteDialog"
                    >
                        {{ t('pay-slips.delete') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        v-if="isApprovable"
                        @click="openApproveDialog"
                    >
                        {{ t('pay-slips.approve') }}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        v-if="isApprovable"
                        @click="openRejectDialog"
                    >
                        {{ t('pay-slips.reject') }}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>

        <AddButton
            v-else-if="canUpdatePayslips"
            @click="openUploadDialog"
            width="narrow"
        >
            {{ t('pay-slips.headers.upload') }}
        </AddButton>

        <span
            v-else
            class="uppercase"
            :class="color"
            >{{ text }}</span
        >

        <PayslipUpload
            v-if="canUpdatePayslips"
            :payslip="payslip"
            v-model="uploadModalOpen"
        />
        <PayslipSendReminder
            v-if="canUpdatePayslips && !isRejected && !isApproved"
            :payslip="payslip"
            v-model="sendReminderModalOpen"
            :selected-company-id="user.selected_company_id"
        />
        <PayslipDelete
            v-if="isUploaded && hasPermission(Permission.SALARY_PAYSLIPS_DELETE)"
            :payslip="payslip"
            v-model="deleteModalOpen"
            :selected-company-id="user.selected_company_id"
        />
        <PayslipApprove
            v-if="isApprovable"
            :payslip="payslip"
            v-model="approveModalOpen"
            :user="user"
        />
        <PayslipReject
            v-if="isApprovable"
            :payslip="payslip"
            v-model="rejectModalOpen"
            :user="user"
        />
    </DataTableActions>
</template>
